/**
 * Workflow Projects — list view.
 *
 * A project groups related workflows into one named pipeline (e.g. Supernova's
 * 5-definition procurement chain). This page lists the org's projects with
 * their member count + entry point, lets an admin create one, run a whole
 * project on demand, or delete it. Drilling into a project (Open) goes to the
 * detail page where members are managed + ordered.
 *
 * The list is paged / searched / sorted SERVER-SIDE via
 * ``GET /workflows/projects/grid`` (usePaginatedData + SearchInput +
 * SortableTableHead + Pagination) — the page never slices, sorts or filters
 * the rows it was given. The Health chip is derived in the browser from the
 * org-wide membership + last-run feeds (no column behind it, so no sort and no
 * filter — D4).
 *
 * Owner/admin only — members get the same no-access view as Definitions.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  AlertCircle,
  ChevronRight,
  FolderTree,
  Loader2,
  MoreHorizontal,
  Play,
  Plus,
  RefreshCw,
  ShieldOff,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";
import {
  usePaginatedData,
  type PaginatedResponse,
  type ServerSortState,
} from "@/hooks/usePaginatedData";
import { usePermissions } from "@/hooks/usePermissions";
import {
  type MembershipEntry,
  type ProjectSortField,
  type WorkflowLastRun,
  type WorkflowProject,
  workflowsApi,
} from "@/services/api/workflows";
import {
  ProjectHealthChip,
  LastRunsUnavailable,
  projectHealth,
} from "@/components/portal/workflow-flow-v2/workflowLastRun";

export const Route = createFileRoute("/portal/workflows/projects")({
  component: ProjectsPage,
});

/** Lowercase, hyphenate, trim — turns a name into a stable project_key so the
 * author doesn't craft one by hand. Idempotent. */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip combining diacritics
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function ProjectsPage() {
  const { token } = useAuth();
  const { isAdmin, isPlatformAdmin } = usePermissions();
  const canManage = isAdmin || isPlatformAdmin;
  const navigate = useNavigate();

  // Grid controls. The search term is debounced before it reaches the hook's
  // dependencies so a pause — not every keystroke — resets to page 1 and
  // refetches once (D12).
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Org-wide membership map + last-run signal, for the per-project health chip.
  const [memberships, setMemberships] = useState<MembershipEntry[]>([]);
  const [lastRuns, setLastRuns] = useState<WorkflowLastRun[]>([]);
  const [lastRunsFailed, setLastRunsFailed] = useState(false);
  // Create/delete failures — separate from the grid fetch's own `error`.
  const [actionError, setActionError] = useState<string | null>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createKey, setCreateKey] = useState("");
  const [keyEdited, setKeyEdited] = useState(false);
  const [createDescription, setCreateDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [deleting, setDeleting] = useState<WorkflowProject | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Project ids currently being run via "Run project" — disables the row's
  // menu item + shows a spinner. A set so two projects can run independently.
  const [runningIds, setRunningIds] = useState<Set<string>>(new Set());

  /**
   * One page of the grid, every axis server-side. Errors propagate: the
   * projects list is authoritative, and its failure errors the page (the hook
   * surfaces it as `error`). Members have no gateway access to /projects —
   * don't fire the call for them (it would 403); the no-access view renders
   * instead.
   */
  const fetchProjects = useCallback(
    async (
      page: number,
      pageSize: number,
      sort?: ServerSortState,
    ): Promise<PaginatedResponse<WorkflowProject>> => {
      if (!canManage) return { data: [], totalCount: 0, page, pageSize };
      const res = await workflowsApi.listProjectsGrid(token ?? undefined, {
        search: debouncedSearch.trim() || undefined,
        sortBy: sort?.key as ProjectSortField | undefined,
        sortOrder: sort?.direction,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      return { data: res.rows, totalCount: res.total, page, pageSize };
    },
    [token, canManage, debouncedSearch],
  );

  const {
    data: projects,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
    requestSort,
    getSortDirection,
  } = usePaginatedData<WorkflowProject>({
    fetchFn: fetchProjects,
    initialPageSize: 25,
    initialSort: { key: "name", direction: "asc" },
    dependencies: [token, debouncedSearch],
  });

  /**
   * The per-key feeds behind the Health chip. Org-wide, key-indexed, no DSL —
   * fetched once on mount and on Refresh rather than per page turn (D11): a
   * page turn brings no new information for them. Best-effort so a hiccup
   * must not blank the project list; array-guarded because `.catch()` only
   * covers a rejected promise, not a 200 of the wrong shape.
   */
  const loadFeeds = useCallback(async () => {
    const [mships, runs] = await Promise.all([
      workflowsApi
        .listProjectMemberships(token ?? undefined)
        .catch(() => [] as MembershipEntry[]),
      workflowsApi
        .listLastRuns(token ?? undefined)
        .then((r) => r.rows)
        .catch(() => null),
    ]);
    setMemberships(Array.isArray(mships) ? mships : []);
    setLastRuns(Array.isArray(runs) ? runs : []);
    setLastRunsFailed(runs === null);
  }, [token]);

  useEffect(() => {
    // Same gate as the grid fetch — members would 403 on every feed.
    if (canManage) void loadFeeds();
  }, [loadFeeds, canManage]);

  /** Refresh = the feeds AND the current page, in that order. */
  const handleRefresh = useCallback(() => {
    void loadFeeds();
    refresh();
  }, [loadFeeds, refresh]);

  /**
   * Refetch after a row LEFT the grid (deleted). If it was this page's only
   * row and we are past page 1, step back instead of refetching the same
   * window — the hook does not move `page` when `total` shrinks, so a plain
   * refresh would land on an empty page past the end.
   */
  const refetchAfterRowLeft = useCallback(() => {
    if (projects.length === 1 && pagination.page > 1) prevPage();
    else refresh();
  }, [projects.length, pagination.page, prevPage, refresh]);

  // Re-pull just the last-run signal after firing a project, so the per-project
  // health chip reflects the runs just triggered without a full reload.
  const refreshLastRuns = useCallback(async () => {
    try {
      const res = await workflowsApi.listLastRuns(token ?? undefined);
      setLastRuns(res.rows);
      setLastRunsFailed(false);
    } catch {
      /* keep the existing signal */
    }
  }, [token]);

  // project_id → member workflow_keys (org-wide membership map).
  const keysByProject = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const e of memberships) {
      const list = m.get(e.project_id) ?? [];
      list.push(e.workflow_key);
      m.set(e.project_id, list);
    }
    return m;
  }, [memberships]);

  const lastRunByKey = useMemo(() => {
    const m = new Map<string, WorkflowLastRun>();
    for (const r of lastRuns) m.set(r.workflow_key, r);
    return m;
  }, [lastRuns]);

  // Keep the key in sync with the name until the user hand-edits the key.
  const effectiveCreateKey = useMemo(
    () => (keyEdited ? createKey : slugify(createName)),
    [keyEdited, createKey, createName],
  );

  const handleCreate = useCallback(async () => {
    const key = effectiveCreateKey.trim();
    if (!createName.trim()) {
      setActionError("Project name is required");
      return;
    }
    if (!key) {
      setActionError("Project key is required");
      return;
    }
    setIsSaving(true);
    setActionError(null);
    try {
      await workflowsApi.createProject(
        {
          project_key: key,
          name: createName.trim(),
          description: createDescription.trim() || null,
        },
        token ?? undefined,
      );
      setIsCreateOpen(false);
      setCreateName("");
      setCreateKey("");
      setKeyEdited(false);
      setCreateDescription("");
      refresh();
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : "Failed to create project",
      );
    } finally {
      setIsSaving(false);
    }
  }, [createDescription, createName, effectiveCreateKey, refresh, token]);

  const handleRunProject = useCallback(
    async (project: WorkflowProject) => {
      setRunningIds((s) => new Set(s).add(project.id));
      try {
        const res = await workflowsApi.runProject(
          project.id,
          token ?? undefined,
        );
        if (res.members_fired === 0) {
          toast.info(
            `${project.name} has no schedule-driven members to run.`,
          );
        } else if (res.executions_fired === 0) {
          toast.info(
            `Ran ${project.name} — ${res.members_fired} member${res.members_fired === 1 ? "" : "s"} scheduled, no work to do right now.`,
          );
        } else {
          const projectId = project.id;
          toast.success(
            `Ran ${project.name} — fired ${res.executions_fired} execution${res.executions_fired === 1 ? "" : "s"} across ${res.members_fired} member${res.members_fired === 1 ? "" : "s"}.`,
            {
              action: {
                label: "View",
                onClick: () =>
                  void navigate({
                    to: "/portal/workflows/executions",
                    search: { project: projectId },
                  }),
              },
            },
          );
        }
        if (res.members_fired > 0) void refreshLastRuns();
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Failed to run project",
        );
      } finally {
        setRunningIds((s) => {
          const next = new Set(s);
          next.delete(project.id);
          return next;
        });
      }
    },
    [token, navigate, refreshLastRuns],
  );

  const handleDelete = useCallback(async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await workflowsApi.deleteProject(deleting.id, token ?? undefined);
      toast.success(`Deleted project ${deleting.name}`);
      setDeleting(null);
      // The membership map lost this project's links too.
      void loadFeeds();
      refetchAfterRowLeft();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to delete project",
      );
    } finally {
      setIsDeleting(false);
    }
  }, [deleting, loadFeeds, refetchAfterRowLeft, token]);

  const hasActiveFilter = debouncedSearch.trim() !== "";

  if (!canManage) {
    return (
      <>
        <Helmet>
          <title>Workflow Projects | Portal</title>
        </Helmet>
        <div className="space-y-6">
          <PageHeader
            title="Workflow Projects"
            description="Group related workflows into one pipeline."
            icon={FolderTree}
            eyebrow="Authoring"
          />
          <Alert>
            <ShieldOff className="h-4 w-4" />
            <AlertDescription>
              Workflows are managed by organisation owners and admins. You don't
              have access to this area.
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Workflow Projects | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="Workflow Projects"
          description="Group related workflows into one pipeline — set the entry point, order the steps, run the whole thing on demand."
          icon={FolderTree}
          eyebrow="Authoring"
        >
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New project
          </Button>
        </PageHeader>

        {(error || actionError) && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error ?? actionError}</AlertDescription>
          </Alert>
        )}

        {lastRunsFailed && <LastRunsUnavailable />}

        {/* One content panel: controls bar, then the table, then the pager.
            Every control below drives a server round-trip — nothing here
            narrows or reorders the page it was given. */}
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
              placeholder="Search by name, key or entry point..."
              containerClassName="flex-1 min-w-[200px]"
              aria-label="Search projects"
              // BE = FE: the grid 422s a search over 200 chars
              // (lara-data-plane workflows/api/v2/grid_params.py); cap it
              // here so the operator never sees that 422 as an empty grid.
              maxLength={200}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              aria-label="Refresh"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>

          {isLoading && projects.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : projects.length === 0 ? (
            <div className="py-12 text-center">
              <FolderTree className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-medium">
                {hasActiveFilter
                  ? "No projects match your search"
                  : "No projects yet"}
              </h3>
              <p className="mb-4 text-muted-foreground">
                {hasActiveFilter
                  ? "Try a different search term."
                  : 'Click "New project" to group your workflows into a pipeline.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    direction={getSortDirection("name")}
                    onSort={() => requestSort("name")}
                  >
                    Name
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("project_key")}
                    onSort={() => requestSort("project_key")}
                  >
                    Key
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("entry_workflow_key")}
                    onSort={() => requestSort("entry_workflow_key")}
                  >
                    Entry point
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("member_count")}
                    onSort={() => requestSort("member_count")}
                  >
                    Members
                  </SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>
                    Health
                  </SortableTableHead>
                  <SortableTableHead
                    direction={null}
                    onSort={() => {}}
                    disabled
                    className="w-[50px]"
                  >
                    {""}
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((project) => (
                  <TableRow key={project.id}>
                    <TableCell>
                      {/* The name opens the project — the row's primary
                          action, one click without the menu. */}
                      <button
                        type="button"
                        className="text-left font-medium hover:underline"
                        onClick={() =>
                          void navigate({
                            to: "/portal/workflows/projects/$projectId",
                            params: { projectId: project.id },
                          })
                        }
                      >
                        {project.name}
                      </button>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {project.project_key}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {project.entry_workflow_key ?? "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{project.member_count}</Badge>
                    </TableCell>
                    <TableCell>
                      <ProjectHealthChip
                        health={projectHealth(
                          keysByProject.get(project.id) ?? [],
                          lastRunByKey,
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Actions for ${project.name}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            disabled={
                              runningIds.has(project.id) ||
                              project.member_count === 0
                            }
                            onClick={() => void handleRunProject(project)}
                            aria-label={`Run project ${project.name}`}
                          >
                            {runningIds.has(project.id) ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Play className="mr-2 h-4 w-4" />
                            )}
                            Run project
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              void navigate({
                                to: "/portal/workflows/projects/$projectId",
                                params: { projectId: project.id },
                              })
                            }
                          >
                            <ChevronRight className="mr-2 h-4 w-4" />
                            Open
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-rose-600 focus:text-rose-700"
                            onClick={() => setDeleting(project)}
                            aria-label={`Delete project ${project.name}`}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {/* Outside the table/empty ternary on purpose: a page past the end
              (its last row just left) shows the empty state AND the pager, so
              the operator can step back. */}
          {pagination.totalCount > 0 && (
            <div className="mt-4 border-t pt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                pageSize={pagination.pageSize}
                pageSizeOptions={pageSizeOptions}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                onPageChange={goToPage}
                onPageSizeChange={setPageSize}
                onFirstPage={firstPage}
                onLastPage={lastPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New project</DialogTitle>
            <DialogDescription>
              Group workflows into a named pipeline. You can add members + set
              the entry point after creating it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Name</Label>
              <Input
                id="project-name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Procurement Pipeline"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-key">Key</Label>
              <Input
                id="project-key"
                value={effectiveCreateKey}
                onChange={(e) => {
                  setKeyEdited(true);
                  setCreateKey(e.target.value);
                }}
                placeholder="procurement-pipeline"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                placeholder="PO landed → Asana card + MyApp rows + payment ticket"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleCreate()} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete project?</DialogTitle>
            <DialogDescription>
              This removes the grouping <strong>{deleting?.name}</strong> and its
              membership links. The workflows themselves are not deleted — they
              just stop being grouped.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleting(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
